import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { ReactNode, useEffect, useState } from "react";
import moment from "moment";
import { useT } from "@hooks";
import RecurrenceDetailsSkeleton from "@components/Skeletons/RecurrenceDetailsSkeleton";
import TransactionSummary, { type SummaryField } from "@components/TransactionSummary";
import { getAuditTransactionDetails } from "@services/audit";
import { getRecurrenceTransactions } from "@services/form";
import { getStatusLabelKeyFromKind, resolveTransactionStatus } from "@utils/transactionStatus";
import {
  STATUS_BADGE_CLASS,
  TimelineRow,
  EMPTY_VALUE,
  buildAmountSummaryColumn,
  buildPartySummaryColumns,
  buildStatusSummaryColumn,
  buildVehicleSummaryColumn,
  formatAmount,
  formatDate,
  formatText,
} from "../transactionDetails";
import type { AuditTransactionDetails } from "../../../types/audit";
import type { IRecurrenceTransactionItem } from "@types";

interface Props {
  show: boolean;
  onClose: () => void;
  module: "DESPESA" | "RECEITA";
  transactionId?: string | null;
  recurrenceId?: string | null;
  isMarketplace?: boolean;
}

const CONTRACT_STATUS_BADGE_CLASS: Record<string, string> = {
  ACTIVE: STATUS_BADGE_CLASS.realizado,
  CANCELED: STATUS_BADGE_CLASS.vencido,
};

const CONTRACT_SUMMARY_SKELETON_COLUMNS = [2, 2, 2, 1, 2, 2];

const unwrapItems = (payload: any): IRecurrenceTransactionItem[] => {
  const data = payload?.data ?? payload;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.transactions)) return data.transactions;

  return [];
};

const sortByDate = (items: IRecurrenceTransactionItem[]): IRecurrenceTransactionItem[] =>
  [...items].sort((a, b) => {
    const first = moment(a.transactionDate ?? a.dueDate);
    const second = moment(b.transactionDate ?? b.dueDate);

    if (!first.isValid() || !second.isValid()) return 0;

    return first.valueOf() - second.valueOf();
  });

const DialogRecurrenceDetails = ({
  show,
  onClose,
  module,
  transactionId,
  recurrenceId,
  isMarketplace = false,
}: Props) => {
  const t = useT();

  const [details, setDetails] = useState<AuditTransactionDetails | null>(null);
  const [items, setItems] = useState<IRecurrenceTransactionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!show) {
      setDetails(null);
      setItems([]);
      setHasError(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show || !recurrenceId) return;

    let isActive = true;

    const loadRecurrence = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const [detailsResponse, transactionsResponse] = await Promise.all([
          transactionId ? getAuditTransactionDetails(transactionId) : Promise.resolve(null),
          getRecurrenceTransactions(recurrenceId),
        ]);

        if (!isActive) return;

        setDetails(detailsResponse);
        setItems(sortByDate(unwrapItems(transactionsResponse)));
      } catch {
        if (!isActive) return;

        setDetails(null);
        setItems([]);
        setHasError(true);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    loadRecurrence();

    return () => {
      isActive = false;
    };
  }, [show, recurrenceId, transactionId]);

  const entityLabel = t(module === "RECEITA" ? "recipe.audit.label.recipe" : "expense.audit.label.expense");
  const showsContract = module === "DESPESA" && isMarketplace;

  const renderContractStatus = (status?: string | null): ReactNode => {
    const normalized = status?.trim().toUpperCase();

    if (!normalized) return EMPTY_VALUE;

    const badgeClass = CONTRACT_STATUS_BADGE_CLASS[normalized];

    return (
      <span className={badgeClass ?? STATUS_BADGE_CLASS.previsto}>
        {badgeClass ? t(`recurrence.details.contractStatus.${normalized}`) : status}
      </span>
    );
  };

  const summaryColumns: SummaryField[][] = [
    [
      {
        icon: "rioglyph-folder-closed",
        label: t("expense.audit.label.category"),
        value: formatText(details?.categoryName),
      },
      {
        icon: "rioglyph-folder-open",
        label: t("expense.audit.label.subcategory"),
        value: formatText(details?.subcategoryName),
      },
    ],
    buildStatusSummaryColumn(t, details, module),
    buildAmountSummaryColumn(t, details),
    ...(showsContract
      ? [
          [
            {
              icon: "rioglyph-id",
              label: t("recurrence.details.label.contractCode"),
              value: formatText(details?.contractCode),
            },
          ],
          [
            {
              icon: "rioglyph-ok-sign",
              label: t("recurrence.details.label.contractStatus"),
              value: renderContractStatus(details?.contractStatus),
            },
            {
              icon: "rioglyph-calendar",
              label: t("recurrence.details.label.contractCanceledAt"),
              value: formatDate(details?.contractCanceledAt),
            },
          ],
          buildVehicleSummaryColumn(t, details),
        ]
      : [
          [
            {
              icon: "rioglyph-building",
              label: t("expense.audit.label.branch"),
              value: formatText(details?.branchName),
            },
            {
              icon: "rioglyph-role-management",
              label: t("expense.audit.label.segment"),
              value: formatText(details?.segmentName),
            },
          ],
          ...buildPartySummaryColumns(t, details),
        ]),
  ];

  const renderCell = (label: string, content: ReactNode, isFirst = false) => (
    <div
      className={`flex-1-1 padding-10 width-100pct-xs ${isFirst ? "" : "border border-left-only border-color-light"}`}
      style={{ minWidth: 0 }}
    >
      <div className="text-bold text-size-14">{label}</div>
      <div className="margin-top-5">{content}</div>
    </div>
  );

  const renderValueBox = (value: string) => (
    <div className="bg-lightest rounded padding-x-10 padding-y-5 text-size-14 text-color-darker">{value}</div>
  );

  const total = items.length;

  return (
    <Dialog
      show={show}
      bsSize="xl"
      title={
        <span className="text-medium text-size-16">
          {t(module === "RECEITA" ? "recurrence.dialog.detailsReceiptTitle" : "recurrence.dialog.detailsExpenseTitle")}
        </span>
      }
      body={
        isLoading ? (
          <RecurrenceDetailsSkeleton summaryColumns={showsContract ? CONTRACT_SUMMARY_SKELETON_COLUMNS : undefined} />
        ) : (
          <>
            <TransactionSummary entityLabel={entityLabel} columns={summaryColumns} />

            <div className="margin-top-15">
              {hasError && (
                <div className="text-center text-color-danger padding-15">
                  {t("recurrence.details.message.loadError")}
                </div>
              )}

              {!hasError && total === 0 && (
                <div className="text-center text-color-gray padding-15">{t("recurrence.details.message.empty")}</div>
              )}

              {[...items].reverse().map((item, index) => {
                const position = total - index;
                const isLast = index === total - 1;
                const statusKind = resolveTransactionStatus(
                  item.status,
                  item.dueDate,
                  item.transactionDate ?? undefined
                );
                const isRealized = statusKind === "realizado";

                return (
                  <TimelineRow
                    key={item.id}
                    isLast={isLast}
                    glyph={
                      <span
                        className={`rioglyph text-bold ${isRealized ? "rioglyph-ok" : "rioglyph-time-alt text-size-20"}`}
                        aria-hidden="true"
                      />
                    }
                  >
                    {renderCell(
                      t("recurrence.details.column.quantity"),
                      renderValueBox(t("recurrence.details.position", { current: position, total })),
                      true
                    )}
                    {renderCell(
                      t("recurrence.details.column.date"),
                      renderValueBox(formatDate(item.transactionDate ?? item.dueDate))
                    )}
                    {renderCell(t("recurrence.details.column.amount"), renderValueBox(formatAmount(item.amount)))}
                    {renderCell(
                      t("recurrence.details.column.status"),
                      <span className={STATUS_BADGE_CLASS[statusKind]}>
                        {t(getStatusLabelKeyFromKind(statusKind, module))}
                      </span>
                    )}
                  </TimelineRow>
                );
              })}
            </div>
          </>
        )
      }
      footer={
        <div className="display-flex justify-content-end">
          <button type="button" className="btn btn-default" onClick={onClose}>
            {t("common.action.close")}
          </button>
        </div>
      }
      onClose={onClose}
      showCloseButton
      useOverflow
    />
  );
};

export default DialogRecurrenceDetails;
